import { type SocietyWorkflowInstanceInvokeParamsInput } from "shared/api/types/society/[societyId]/workflow/[workflowId]/instance";
import { type PillarFormInputComponentConfig } from "shared/components/pillar-form/PillarFormInputComponentConfig";

import { type ParticipantWorkflowChangeParticipantsFunctionRoleConfig } from "shared/types/workflow";
import {
  type WorkflowActionForm,
  type WorkflowActionModal,
  type WorkflowActionHyperlink,
} from "shared/workflow/workflow-action";
import { type WorkflowStatus } from "shared/workflow/workflow-instance";

export type WorkflowFunctionStateInvokeParamsBase<
  T extends Record<string, any> = Record<string, any>,
> = T & SocietyWorkflowInstanceInvokeParamsInput;
export type WorkflowStateConfigBase<
  T extends Record<string, any> = Record<string, any> & any,
> = T;
// Base interfaces for workflow definition, state, transitions, and parameters.
export enum WorkflowStateType {
  FUNCTION = "function",
  WAIT = "wait",
  CONDITIONAL_STATE_HISTORY = "conditional_state_history",
  CONDITIONAL_PAYLOAD_VALUE = "conditional_payload_value",
}
export type WorkflowStateCommon =
  | WorkflowFunctionState
  | WorkflowWaitState
  | WorkflowStateHistoryConditionState
  | WorkflowPayloadValueConditionState;

export interface WorkflowStateAdminForm {
  formElements: PillarFormInputComponentConfig[];
}

export interface WorkflowStateBase {
  description: string;
  type: WorkflowStateType;
  name: string;
  adminForm?: WorkflowStateAdminForm;
  /**
   * Sort key for the admin state list, ascending, 0 when unset. Must be an
   * integer: the database casts it when a definition is saved, so a fraction
   * fails the write.
   */
  adminDisplayPriority?: number;
}

export interface WorkflowWaitState extends WorkflowStateBase {
  type: WorkflowStateType.WAIT;
  status: WorkflowStatus;
  userhubRedirect?: string;
  formCompleteTemplate?: string;
  possibleActions?: (
    | WorkflowActionForm
    | WorkflowActionModal
    | WorkflowActionHyperlink
  )[];
  nextState?: WorkflowStateTransition;
  updateChildren?: boolean;
}

export interface WorkflowStateHistoryConditionState extends WorkflowStateBase {
  condition: WorkflowStateHistoryCondition[];
  type: WorkflowStateType.CONDITIONAL_STATE_HISTORY;
}

export interface WorkflowPayloadValueConditionState extends WorkflowStateBase {
  nextState: WorkflowStateTransition;
  condition: WorkflowPayloadValueCondition[];
  type: WorkflowStateType.CONDITIONAL_PAYLOAD_VALUE;
}

export interface WorkflowPayloadValueCondition extends WorkflowStateTransition {
  property: string;
}

export interface WorkflowFunctionState<WorkflowStateConfig = any>
  extends WorkflowStateBase {
  transitions?: WorkflowStateTransition[];
  function: string;
  type: WorkflowStateType.FUNCTION;
  config: WorkflowStateConfig;
}

export interface WorkflowStateTransition {
  //TODO: Figure out a way for the ParticipantWorkflow function to be typed to add this role but not ever function
  role?: ParticipantWorkflowChangeParticipantsFunctionRoleConfig[];
  event: string;
  next: string;
}

export interface WorkflowStateHistoryCondition extends WorkflowStateTransition {
  stateName: string;
}

/**
 * One event that puts an instance ON a state, paired with the state it came
 * from. Derived by the database from every other state's outbound transitions
 * (the `entry_event` column on WorkflowDefinitionMeta) rather than authored in
 * a seed, so it only ever appears on a state read back from the API.
 */
export interface WorkflowStateEntryEvent {
  event: string;
  fromStateName: string;
}
